import { useSyncExternalStore } from 'react';

/**
 * A short two-tone chime when a new SOS arrives (Web Audio, no sound file). On by default; staff can turn it off on
 * the SOS page. Browsers only play sound after the person has used the page once — signing in counts.
 */
const KEY = 'zr.admin.sosSound';
const listeners = new Set<() => void>();

function read(): boolean {
  try {
    return localStorage.getItem(KEY) !== 'off';
  } catch {
    return true;
  }
}
let on = typeof window !== 'undefined' ? read() : true;

export function setSosSound(next: boolean) {
  on = next;
  try {
    localStorage.setItem(KEY, next ? 'on' : 'off');
  } catch {
    /* private mode: keep it for this visit */
  }
  for (const l of listeners) l();
}

export function useSosSound(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => on,
    () => on,
  );
}

let ctx: AudioContext | null = null;
export function playSosChime() {
  if (!on || typeof window === 'undefined') return;
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    ctx ??= new AC();
    const now = ctx.currentTime;
    for (const [i, freq] of [880, 660, 880, 660].entries()) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = now + i * 0.22;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.18, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.21);
    }
  } catch {
    /* no audio: the red strip still shows */
  }
}
